import Head from 'next/head';
import { Summary } from '@components/open-position/Summary';
import Form from '@components/open-position/Form';
import { useEffect, useState } from 'react';
import { useContracts, useAddresses, useMinimumDepositIncreased, useNumOfCycles } from '@components/store/getters';
import { dappStore, positionStore } from '@components/store';
import Modal from '@components/common/Modal';
import { QuestionLink } from '@components/containers/open-position/components/QuestionLink';
import {
  Alert,
  Container,
  FormContainer,
  Main,
  PageTitle,
  QuestionsContainer,
  QuestionsTitle,
  Section,
} from './open-position.styles';
import { roundNumber } from 'utils/roundNumber';
import { formatBigNumber } from 'utils/formatBigNumber';
import { useDebounce } from 'hooks/useDebounce';
import { toBigNumber } from 'utils/toBigNumber';
import { OptionType } from '@components/common/Dropdown';
import ErrorBox from '@components/open-position/ErrorBox';
import { useWeb3Methods } from 'hooks/useWeb3Methods';
import { DebugMenu } from '@components/common/DebugMenu';
import { useModal } from 'hooks/useModal';

export interface StableCoinOption extends OptionType {
  address?: string;
  icon?: any;
  config?: { type?: string; withDescription?: boolean };
  selected?: boolean;
  isDropdownOpen?: boolean;
}

export default function OpenPosition() {
  const [deposit, setDeposit] = useState('0');
  const [estimatedPosition, setEstimatedPosition] = useState(0);
  const [tokenFeeInArch, setTokenFeeInArch] = useState(null);
  const [tokenFeeInUsd, setTokenFeeInUsd] = useState(null);
  const [collateral, setCollateral] = useState(0);
  const [selectedStableCoin, setSelectedStableCoin] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [selectedLeverage, setSelectedLeverage] = useState(null);
  const [useArchFromWallet, setUseArchFromWallet] = useState(false);
  const contracts = useContracts();
  const addresses = useAddresses();
  const debouncedDeposit = useDebounce(deposit, 800);
  const numOfCycles = useNumOfCycles();
  const minimumDepositIncreased = useMinimumDepositIncreased();
  const { retrieveTokenFeeInArch, retrieveTokenFeeInUsd } = useWeb3Methods();
  const { modalComponent, isModalOpen, closeModal, modalTitle, hideCloseButton, showLoader } = useModal();

  const retrieveEstimatedPosition = async () => {
    if (collateral === 0) {
      setEstimatedPosition(0);
    } else {
      const paramStore = contracts.parameterStore;
      const collateralIn18Dec = toBigNumber(collateral);
      const estimatePosition = await paramStore.getAllowedLeverageForPosition(collateralIn18Dec, numOfCycles);
      const estimatedPositionFormatted = Number(formatBigNumber(estimatePosition));
      setEstimatedPosition(estimatedPositionFormatted);
    }
  };

  const retrieveFees = async () => {
    const { collateral, archTokenFeeInArch } = await retrieveTokenFeeInArch(
      selectedStableCoin.value,
      debouncedDeposit,
      numOfCycles,
      selectedStableCoin.address,
      useArchFromWallet,
    );
    const archTokenFeeInUsd = await retrieveTokenFeeInUsd(
      selectedStableCoin.value,
      debouncedDeposit,
      numOfCycles,
      selectedStableCoin.address,
    );
    const tokenFeeInArchFormatted = roundNumber(Number(archTokenFeeInArch), 2);
    setTokenFeeInUsd(roundNumber(Number(archTokenFeeInUsd), 2));
    setCollateral(roundNumber(Number(collateral), 2));
    setTokenFeeInArch(tokenFeeInArchFormatted);
    positionStore.setState({ openPositionArchNeeded: tokenFeeInArchFormatted });
  };

  useEffect(() => {
    const debouncedDepositAmount = Number(debouncedDeposit);
    if (contracts?.zapper && debouncedDepositAmount && addresses) {
      retrieveFees();
      const interval = setInterval(() => {
        retrieveFees();
      }, 3000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [contracts?.zapper, addresses, debouncedDeposit, selectedStableCoin, useArchFromWallet]);

  useEffect(() => {
    const debouncedDepositAmount = Number(debouncedDeposit);
    if (debouncedDepositAmount === 0) {
      setEstimatedPosition(0);
      setTokenFeeInArch(0);
      setTokenFeeInArch(0);
      setCollateral(0);
    }
  }, [debouncedDeposit]);

  useEffect(() => {
    if (contracts?.parameterStore && collateral) {
      retrieveEstimatedPosition();
    }
  }, [contracts?.parameterStore, collateral]);

  useEffect(() => {
    if (minimumDepositIncreased) {
      setDeposit(minimumDepositIncreased.toString());
    }
  }, [minimumDepositIncreased]);

  useEffect(() => {
    if (modalTitle === 'Route & Fee Details') {
      dappStore.setState({
        modalProps: {
          tokenFeeInArch,
          tokenFeeInUsd,
          deposit: debouncedDeposit,
          collateral,
          selectedStableCoin,
          estimatedPosition,
        },
      });
    }
  }, [modalTitle, tokenFeeInArch, debouncedDeposit, collateral, selectedStableCoin]);

  return (
    <>
      <Head>
        <title>Open New Position</title>
        <meta name="description" content="Open a new leveraged position using ArchimedesFi" />
      </Head>
      <Container>
        <ErrorBox hideCloseButton />
        <PageTitle type="h4">Open Position</PageTitle>
        {process.env.NEXT_PUBLIC_ENVMODE !== 'PRODUCTION' && <DebugMenu />}
        <Main>
          <Section>
            <MobileMessage />
            <FormContainer>
              <Form
                deposit={deposit}
                setDeposit={setDeposit}
                estimatedPosition={estimatedPosition}
                selectedStableCoin={selectedStableCoin}
                setSelectedStableCoin={setSelectedStableCoin}
                selectedStrategy={selectedStrategy}
                setSelectedStrategy={setSelectedStrategy}
                selectedLeverage={selectedLeverage}
                setSelectedLeverage={setSelectedLeverage}
                tokenFeeInArch={tokenFeeInArch}
                setUseArchFromWallet={setUseArchFromWallet}
                useArchFromWallet={useArchFromWallet}
                collateral={collateral}
              />
            </FormContainer>
          </Section>
          <Summary
            estimatedPosition={estimatedPosition}
            collateral={collateral}
            tokenFeeInArch={tokenFeeInArch}
            tokenFeeInUsd={tokenFeeInUsd}
            useArchFromWallet={useArchFromWallet}
          />
        </Main>
        <QuestionsContainer>
          <QuestionsTitle type="h4">Still have questions?</QuestionsTitle>
          <QuestionLink target="_blank" variant="thick" type="doc" href="https://docs.archimedesfi.com">
            Read User Docs
          </QuestionLink>
          <QuestionLink target="_blank" variant="thick" type="discord" href="https://discord.gg/s7Sxd2CfHW">
            Chat with us on Discord
          </QuestionLink>
        </QuestionsContainer>
      </Container>
      {isModalOpen && (
        <Modal title={modalTitle} onClose={closeModal} withLoader={showLoader} hideCloseButton={hideCloseButton}>
          {modalComponent}
        </Modal>
      )}
    </>
  );
}

function MobileMessage() {
  return (
    <Alert type="info">
      <strong>View on a desktop to open a position.</strong>
    </Alert>
  );
}
